import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Trash2 as DeleteIcon,
  Eye as VisibilityIcon,
  FileText as ArticleIcon,
  FileText as DocumentIcon,
  Printer as FaxIcon,
} from 'lucide-react';
import api from '../../utils/axios';
import { SendFaxModal, FaxStatusPanel } from '../Fax';

const AppealsList = () => {
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [faxModalOpen, setFaxModalOpen] = useState(false);
  const [faxAppeal, setFaxAppeal] = useState(null);
  const [lastSentFaxId, setLastSentFaxId] = useState(null);
  const [defaultFromNumber, setDefaultFromNumber] = useState('');

  const fetchAppeals = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/documents/appeals/');
      setAppeals(response.data.appeals || []);
    } catch (err) {
      setError('Failed to fetch appeals');
      console.error('Error fetching appeals:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppeals();
  }, []);

  useEffect(() => {
    const loadFaxConfig = async () => {
      try {
        const { getFaxConfig } = await import('../../utils/faxApi');
        const list = await getFaxConfig();
        const cfg = Array.isArray(list) ? list[0] : list?.results?.[0];
        if (cfg?.default_sender_number) setDefaultFromNumber(cfg.default_sender_number);
      } catch {
        // ignore
      }
    };
    loadFaxConfig();
  }, []);

  const handleDeleteClick = (appeal) => {
    setSelectedAppeal(appeal);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAppeal) return;

    try {
      setDeleting(true);
      await api.delete(`/documents/appeals/${selectedAppeal.id}/`);
      setAppeals(appeals.filter(appeal => appeal.id !== selectedAppeal.id));
      setDeleteDialogOpen(false);
      setSuccess('Appeal deleted successfully!');
    } catch (err) {
      setError('Failed to delete appeal');
      console.error('Error deleting appeal:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleViewClick = (appeal) => {
    setSelectedAppeal(appeal);
    setViewDialogOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
        Appeal Letters
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Claim Number</TableCell>
              <TableCell>Patient Name</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {appeals.map((appeal) => (
              <TableRow key={appeal.id}>
                <TableCell>{appeal.claim_number || 'N/A'}</TableCell>
                <TableCell>{appeal.patient_name || 'N/A'}</TableCell>
                <TableCell>{appeal.template_name || 'N/A'}</TableCell>
                <TableCell>{formatDate(appeal.created_at)}</TableCell>
                <TableCell>
                  <Chip
                    label={appeal.appeal_content ? 'Generated' : 'Pending'}
                    color={appeal.appeal_content ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="View Appeal">
                    <IconButton
                      color="primary"
                      onClick={() => handleViewClick(appeal)}
                    >
                      <VisibilityIcon size={16} />
                    </IconButton>
                  </Tooltip>
                  {appeal.appeal_letter && (
                    <Tooltip title="Download Letter">
                      <IconButton
                        color="info"
                        onClick={() => window.open(appeal.appeal_letter, '_blank')}
                      >
                        <DocumentIcon size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {appeal.appeal_letter && (
                    <Tooltip title="Send by fax">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setFaxAppeal(appeal);
                          setFaxModalOpen(true);
                        }}
                      >
                        <FaxIcon size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete Appeal">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(appeal)}
                    >
                      <DeleteIcon size={16} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {appeals.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 4 }}>
                    <ArticleIcon size={48} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Appeal Letters Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No appeal letters have been generated yet.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Appeal Letter</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this appeal letter for claim "{selectedAppeal?.claim_number}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleting}
            variant="contained"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <SendFaxModal
        open={faxModalOpen}
        onClose={(created) => {
          setFaxModalOpen(false);
          setFaxAppeal(null);
          if (created?.id) setLastSentFaxId(created.id);
        }}
        appeal={faxAppeal}
        defaultFromNumber={defaultFromNumber}
      />
      {lastSentFaxId && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Last sent fax</Typography>
          <FaxStatusPanel faxId={lastSentFaxId} onRetrySuccess={() => fetchAppeals()} />
        </Box>
      )}

      {/* View Appeal Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Appeal Letter for Claim {selectedAppeal?.claim_number}
        </DialogTitle>
        <DialogContent>
          {selectedAppeal && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader
                    title="Appeal Letter Details"
                    subheader={
                      <Box>
                        <Typography variant="caption" display="block">
                          Created: {formatDate(selectedAppeal.created_at)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Patient: {selectedAppeal.patient_name}
                        </Typography>
                        {selectedAppeal.template_name && (
                          <Typography variant="caption" display="block">
                            Template: {selectedAppeal.template_name}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <CardContent>
                    {selectedAppeal.appeal_content ? (
                      <Box sx={{
                        maxHeight: 500,
                        overflow: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 2,
                        backgroundColor: '#fafafa'
                      }}>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            lineHeight: 1.5
                          }}
                        >
                          {selectedAppeal.appeal_content}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No appeal content available
                      </Typography>
                    )}
                    {selectedAppeal.appeal_letter && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<DocumentIcon size={16} />}
                          href={selectedAppeal.appeal_letter}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download Appeal Letter
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppealsList;
